import { useEffect } from 'react'

import { prefetchFirstOrderPage } from '@susu/order-list'
import { useQueryClient } from '@tanstack/react-query'

import type { Environment } from '../App'

/**
 * Warms the order history's first page while the advisor is reading the
 * overview. The Orders tab only mounts its list once it becomes active, so
 * the first request used to start on the click — this spends otherwise-idle
 * network on the page the list paints with, and the tab opens on cached data
 * instead of a skeleton.
 *
 * First page only, by design — the background walk for the rest of the
 * history is deliberately not prefetched (see `remainingOrdersQuery`), so a
 * customer who never opens Orders never pays for it.
 *
 * Gated on the overview tab. A customer without `CH-active-cards` never lands
 * there (`getRoleTabs`/`getDefaultTab` keep them on Orders, where the list
 * fetches for itself anyway), so this only warms the cache for a style advisor
 * or a customer with the flag on. A fresh cache entry makes this a no-op, so
 * re-running it is free.
 */
export function useWarmOrderHistory({
  activeTab,
  accountNumber,
  env,
  authToken,
}: {
  activeTab: string
  accountNumber?: string
  env: Environment
  authToken?: string
}) {
  const queryClient = useQueryClient()

  useEffect(() => {
    if (activeTab !== 'overview' || !accountNumber) return

    void prefetchFirstOrderPage(queryClient, { accountNumber, env, authToken })
  }, [activeTab, accountNumber, env, authToken, queryClient])
}
